/**
 * Which of a turn's sources a piece of text actually cites, and how the cited
 * ones renumber for display.
 *
 * One home for the rule so every surface agrees: the citation renderer
 * (useCitationMarkdown), the document panel's grounding line and the chat's
 * document chip all count the same thing — the distinct citation numbers the
 * reader sees — not every source the turn fetched.
 */
import type { ChatMessage } from '@/stores/assistant-store'
import { normalizeCitationMarkdown } from '@/lib/remark-citations'
import { parseUsedCitations, parseUsedLibraryCitations } from '@/lib/parse-used-citations'
import { createCitationMapping, type CitationMapping } from '@/lib/create-citation-mapping'

export interface CitationDisplayMapping {
  /** Original indices in order of first use in the content. */
  usedCitations: number[]
  /** Original index → sequential display index. */
  origToDisplay: Map<number, number>
}

/**
 * Tender (`[N]`) markers → display numbers. `normalizedContent` must be the
 * output of normalizeCitationMarkdown — the same string that gets rendered.
 * A marker with no matching source is not mapped (it renders as nothing).
 */
export function buildTenderCitationMapping(
  normalizedContent: string,
  sources: ChatMessage['sources']
): CitationDisplayMapping & Pick<CitationMapping, 'displayToOrig'> {
  if (!sources || sources.length === 0) {
    return {
      usedCitations: [],
      origToDisplay: new Map<number, number>(),
      displayToOrig: new Map<number, number>(),
    }
  }
  const usedCitations = parseUsedCitations(normalizedContent)
  return { usedCitations, ...createCitationMapping(usedCitations, sources) }
}

/** Library (`[LN]`) markers → display numbers. Same contract as the tender one. */
export function buildLibraryCitationMapping(
  normalizedContent: string,
  librarySources: ChatMessage['librarySources']
): CitationDisplayMapping {
  if (!librarySources || librarySources.length === 0) {
    return { usedCitations: [], origToDisplay: new Map<number, number>() }
  }
  const usedCitations = parseUsedLibraryCitations(normalizedContent)
  const origToDisplay = new Map<number, number>()
  let displayIdx = 1
  for (const origIdx of usedCitations) {
    if (librarySources[origIdx - 1]) {
      origToDisplay.set(origIdx, displayIdx++)
    }
  }
  return { usedCitations, origToDisplay }
}

/**
 * Distinct sources the content cites (tender + library) — the number of
 * citation numbers a reader sees in it. Raw content is fine: it is normalized
 * here exactly as the renderer normalizes it.
 */
export function countCitedSources(
  content: string,
  sources: ChatMessage['sources'],
  librarySources: ChatMessage['librarySources']
): number {
  const normalized = normalizeCitationMarkdown(content)
  return (
    buildTenderCitationMapping(normalized, sources).origToDisplay.size +
    buildLibraryCitationMapping(normalized, librarySources).origToDisplay.size
  )
}
